export const API_BASE_URL = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '');

function getAuthHeaders() {
  const raw = localStorage.getItem('access_token');
  const token = raw ? JSON.parse(raw) : null;
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function buildUrl(path, params = {}) {
  const url = new URL(`${API_BASE_URL}${path}`);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.append(k, v);
  });
  return url;
}

async function handleResponse(response) {
  if (!response.ok) {
    let errorPayload = null;
    try {
      errorPayload = await response.json();
    } catch (_) {
      errorPayload = { message: `HTTP ${response.status}` };
    }
    throw errorPayload;
  }
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

async function safeFetch(url, init = {}) {
  try {
    const response = await fetch(url, init);
    return await handleResponse(response);
  } catch (err) {
    throw {
      error: 'Fleet API Error',
      message: err?.message || 'Request failed',
      details: err,
    };
  }
}

function unwrapList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
}

function sortByNewest(items) {
  return [...items].sort((a, b) => {
    const aDate = new Date(a?.createdAt || a?.date || 0).getTime();
    const bDate = new Date(b?.createdAt || b?.date || 0).getTime();
    return bDate - aDate;
  });
}

function jsonBody(payload) {
  return {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  };
}

export const fleetApi = {
  unwrapList,

  async listMaintenanceLogs(params = {}) {
    return safeFetch(buildUrl('/maintenance-logs', params), { headers: getAuthHeaders() });
  },
  async createMaintenanceLog(payload) {
    return safeFetch(`${API_BASE_URL}/maintenance-logs`, jsonBody(payload));
  },

  async listVehicles(params = {}) {
    return safeFetch(buildUrl('/vehicles', params), { headers: getAuthHeaders() });
  },
  async createVehicle(payload) {
    return safeFetch(`${API_BASE_URL}/vehicles`, jsonBody(payload));
  },
  async updateVehicle(id, payload) {
    return safeFetch(`${API_BASE_URL}/vehicles/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },
  async deleteVehicle(id) {
    return safeFetch(`${API_BASE_URL}/vehicles/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  },

  async listDrivers(params = {}) {
    return safeFetch(buildUrl('/drivers', params), { headers: getAuthHeaders() });
  },
  async createDriver(payload) {
    return safeFetch(`${API_BASE_URL}/drivers`, jsonBody(payload));
  },
  async updateDriver(id, payload) {
    return safeFetch(`${API_BASE_URL}/drivers/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },
  async deleteDriver(id) {
    return safeFetch(`${API_BASE_URL}/drivers/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  },

  async listTrips(params = {}) {
    return safeFetch(buildUrl('/trips', params), { headers: getAuthHeaders() });
  },
  async completeTrip(id, payload) {
    return safeFetch(`${API_BASE_URL}/trips/${id}/complete`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },
  async deleteTrip(id) {
    return safeFetch(`${API_BASE_URL}/trips/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  },

  async listFuels(params = {}) {
    return safeFetch(buildUrl('/fuels', params), { headers: getAuthHeaders() });
  },
  async createFuel(payload) {
    return safeFetch(`${API_BASE_URL}/fuels`, jsonBody(payload));
  },
  async updateFuel(id, payload) {
    return safeFetch(`${API_BASE_URL}/fuels/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },
  async deleteFuel(id) {
    return safeFetch(`${API_BASE_URL}/fuels/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  },
  async approveFuel(id) {
    return safeFetch(`${API_BASE_URL}/fuels/${id}/approve`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
    });
  },
  async rejectFuel(id, reason) {
    return safeFetch(`${API_BASE_URL}/fuels/${id}/reject`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason }),
    });
  },
  async allocateFuel(id, payload) {
    return safeFetch(`${API_BASE_URL}/fuels/${id}/allocate`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },

  async listCompliances(params = {}) {
    return safeFetch(buildUrl('/compliances', params), { headers: getAuthHeaders() });
  },
  async createCompliance(payload) {
    return safeFetch(`${API_BASE_URL}/compliances`, jsonBody(payload));
  },
  async updateCompliance(id, payload) {
    return safeFetch(`${API_BASE_URL}/compliances/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },
  async deleteCompliance(id) {
    return safeFetch(`${API_BASE_URL}/compliances/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  },
  async testComplianceEmail(id) {
    return safeFetch(`${API_BASE_URL}/compliances/${id}/test-email`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
  },

  async listVehicleTypes() {
    return safeFetch(`${API_BASE_URL}/vehicle-types`, { headers: getAuthHeaders() });
  },
  async createVehicleType(payload) {
    return safeFetch(`${API_BASE_URL}/vehicle-types`, jsonBody(payload));
  },
  async updateVehicleType(id, payload) {
    return safeFetch(`${API_BASE_URL}/vehicle-types/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },
  async deleteVehicleType(id) {
    return safeFetch(`${API_BASE_URL}/vehicle-types/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  },
  async listVehicleStatuses() {
    return safeFetch(`${API_BASE_URL}/vehicle-status`, { headers: getAuthHeaders() });
  },
  async createVehicleStatus(payload) {
    return safeFetch(`${API_BASE_URL}/vehicle-status`, jsonBody(payload));
  },
  async updateVehicleStatus(id, payload) {
    return safeFetch(`${API_BASE_URL}/vehicle-status/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },
  async deleteVehicleStatus(id) {
    return safeFetch(`${API_BASE_URL}/vehicle-status/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  },
  async listFuelTypes() {
    return safeFetch(`${API_BASE_URL}/fuel-types`, { headers: getAuthHeaders() });
  },
  async createFuelType(payload) {
    return safeFetch(`${API_BASE_URL}/fuel-types`, jsonBody(payload));
  },
  async updateFuelType(id, payload) {
    return safeFetch(`${API_BASE_URL}/fuel-types/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },
  async deleteFuelType(id) {
    return safeFetch(`${API_BASE_URL}/fuel-types/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  },
  async listMaintenanceTypes() {
    return safeFetch(`${API_BASE_URL}/maintenance-types`, { headers: getAuthHeaders() });
  },
  async createMaintenanceType(payload) {
    return safeFetch(`${API_BASE_URL}/maintenance-types`, jsonBody(payload));
  },
  async updateMaintenanceType(id, payload) {
    return safeFetch(`${API_BASE_URL}/maintenance-types/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },
  async deleteMaintenanceType(id) {
    return safeFetch(`${API_BASE_URL}/maintenance-types/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  },
  async listComplianceTypes() {
    return safeFetch(`${API_BASE_URL}/compliance-types`, { headers: getAuthHeaders() });
  },
  async createComplianceType(payload) {
    return safeFetch(`${API_BASE_URL}/compliance-types`, jsonBody(payload));
  },
  async updateComplianceType(id, payload) {
    return safeFetch(`${API_BASE_URL}/compliance-types/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },
  async deleteComplianceType(id) {
    return safeFetch(`${API_BASE_URL}/compliance-types/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  },
  async listUsers() {
    return safeFetch(`${API_BASE_URL}/users`, { headers: getAuthHeaders() });
  },

  // ─── Scheduled Maintenance ────────────────────────────────────────────────
  async listScheduledMaintenances(params = {}) {
    return safeFetch(buildUrl('/scheduled-maintenance', params), { headers: getAuthHeaders() });
  },
  async createScheduledMaintenance(payload) {
    return safeFetch(`${API_BASE_URL}/scheduled-maintenance`, jsonBody(payload));
  },
  async updateScheduledMaintenance(id, payload) {
    return safeFetch(`${API_BASE_URL}/scheduled-maintenance/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
  },
  async approveScheduledMaintenance(id) {
    return safeFetch(`${API_BASE_URL}/scheduled-maintenance/${id}/approve`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
    });
  },
  async rejectScheduledMaintenance(id, notes) {
    return safeFetch(`${API_BASE_URL}/scheduled-maintenance/${id}/reject`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ notes }),
    });
  },
  async allocateScheduledMaintenance(id) {
    return safeFetch(`${API_BASE_URL}/scheduled-maintenance/${id}/allocate`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
    });
  },
  async completeScheduledMaintenance(id, notes) {
    return safeFetch(`${API_BASE_URL}/scheduled-maintenance/${id}/complete`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ notes }),
    });
  },
  async deleteScheduledMaintenance(id) {
    return safeFetch(`${API_BASE_URL}/scheduled-maintenance/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  },

  // ─── Vehicle Driver Assignment ────────────────────────────────────────────
  async getVehicleDrivers(vehicleId) {
    return safeFetch(`${API_BASE_URL}/drivers/vehicle/${vehicleId}/drivers`, { headers: getAuthHeaders() });
  },
  async assignDriverToVehicle(vehicleId, driverId) {
    return safeFetch(`${API_BASE_URL}/drivers/assign-vehicle`, jsonBody({ vehicleId, driverId }));
  },
  async unassignDriverFromVehicle(vehicleId, driverId) {
    return safeFetch(`${API_BASE_URL}/drivers/unassign-vehicle`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ vehicleId, driverId }),
    });
  },
  async getDriverVehicles(driverId) {
    return safeFetch(`${API_BASE_URL}/drivers/${driverId}/vehicles`, { headers: getAuthHeaders() });
  },

  async getFleetDashboard() {
    const [vehiclesRes, driversRes, tripsRes, fuelsRes, compliancesRes] = await Promise.all([
      this.listVehicles({ page: 1, limit: 100 }),
      this.listDrivers({ page: 1, limit: 100 }),
      this.listTrips({ page: 1, limit: 100 }),
      this.listFuels({ page: 1, limit: 100 }),
      this.listCompliances({ page: 1, limit: 100 }),
    ]);

    const vehicles = unwrapList(vehiclesRes);
    const drivers = unwrapList(driversRes);
    const trips = sortByNewest(unwrapList(tripsRes));
    const fuels = sortByNewest(unwrapList(fuelsRes));
    const compliances = unwrapList(compliancesRes);

    return {
      vehicles,
      drivers,
      trips,
      fuels,
      compliances,
      stats: {
        totalVehicles: vehicles.length,
        activeTrips: trips.filter((t) => !t.endTime).length,
        totalDrivers: drivers.length,
        pendingFuelRequests: fuels.filter((f) => f.status === 'PENDING').length,
        expiringCompliance: compliances.filter((c) => c.status === 'EXPIRING_SOON').length,
      },
    };
  },
};

export default fleetApi;
